import type { Client } from "./client";
import type { Product } from "./product";

export type QuotationStatus =
  | "draft"
  | "sent"
  | "accepted"
  | "rejected"
  | "expired"
  | "converted";

export const QUOTATION_STATUS_LABELS: Record<QuotationStatus, string> = {
  draft: "Borrador",
  sent: "Enviado",
  accepted: "Aceptado",
  rejected: "Rechazado",
  expired: "Expirado",
  converted: "Convertido",
};

export const QUOTATION_STATUS_COLORS: Record<QuotationStatus, string> = {
  draft: "bg-gray-100 text-gray-800",
  sent: "bg-blue-100 text-blue-800",
  accepted: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  expired: "bg-yellow-100 text-yellow-800",
  converted: "bg-purple-100 text-purple-800",
};

export interface Quotation {
  id: string;
  quotation_number: number;
  client_id: string;
  created_by: string;

  // Delivery address (snapshot)
  delivery_address_id: string | null;
  delivery_street: string | null;
  delivery_number: string | null;
  delivery_city: string | null;
  delivery_state: string | null;
  delivery_comuna: string | null;

  // Pricing
  total_amount: number;
  price_with_iva: number | null;
  payment_terms: number;
  sales_condition_id: string | null;

  // Validity
  validity_days: number;
  valid_until: string;

  // Status
  status: QuotationStatus;

  // Conversion tracking
  converted_to_sales_request_id: string | null;
  converted_at: string | null;
  converted_by: string | null;

  // Notes
  comments: string | null;
  internal_notes: string | null;
  rejection_reason: string | null;

  // Timestamps
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface QuotationItem {
  id: string;
  quotation_id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  discount_percent: number;
  created_at: string;
}

export interface QuotationItemWithProduct extends QuotationItem {
  product?: Product;
}

export interface QuotationWithDetails extends Quotation {
  client?: Client;
  items?: QuotationItemWithProduct[];
  created_by_profile?: {
    id: string;
    name: string;
    lastname: string;
  };
  sales_condition?: {
    id: string;
    name: string;
    payment_terms: number;
  };
}

// For creating a new quotation
export interface CreateQuotationInput {
  client_id: string;
  delivery_address_id?: string;
  delivery_street?: string;
  delivery_number?: string;
  delivery_city?: string;
  delivery_state?: string;
  delivery_comuna?: string;
  payment_terms: number;
  sales_condition_id?: string;
  validity_days?: number;
  comments?: string;
  internal_notes?: string;
  items: CreateQuotationItemInput[];
}

export interface CreateQuotationItemInput {
  product_id: string;
  quantity: number;
  unit_price: number;
  discount_percent?: number;
}

// For updating an existing quotation
export interface UpdateQuotationInput {
  delivery_address_id?: string;
  delivery_street?: string;
  delivery_number?: string;
  delivery_city?: string;
  delivery_state?: string;
  delivery_comuna?: string;
  payment_terms?: number;
  sales_condition_id?: string;
  validity_days?: number;
  valid_until?: string;
  status?: QuotationStatus;
  comments?: string;
  internal_notes?: string;
  rejection_reason?: string;
}

// Helper to check if a quotation can be edited
export function canEditQuotation(status: QuotationStatus): boolean {
  return status === "draft" || status === "sent";
}

// Helper to check if a quotation can be converted to sales request
export function canConvertQuotation(status: QuotationStatus): boolean {
  return status === "draft" || status === "sent" || status === "accepted";
}

// Helper to check if a quotation can be deleted
export function canDeleteQuotation(status: QuotationStatus): boolean {
  return status === "draft";
}
